import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex, Radio } from 'antd';
import { RadioChangeEvent } from 'antd/es/radio/interface';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DividerHeader } from '../../../common/components/dividerHeader/DividerHeader';
import { AlgorithmTargetRuleType } from '../../types';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import { AlgorithmType } from '../schema/zAlgorithmType';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import translations from './AlgorithmTargetFormModal.translations.json';
import { AlgorithmTargetMapTypeField } from './AlgorithmTargetMapTypeField';
import { AlgorithmTargetMappingsGraph } from './graph/AlgorithmTargetMappingsGraph';
import { createDefaultTargetRuleFormData } from './schema/defaultValues';
import { AlgorithmTargetRuleFormDataType } from './schema/types';
import { getMaxRuleCode } from './utils';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-target-modal',
);
addTranslations(translations);

export interface AlgorithmTargetMapTypeFieldsProps {
  algorithmTypeCode: AlgorithmType;
  maxRuleCode: number;
  sources: AlgorithmSourceFormDataType[];
  mappings: AlgorithmMappingQueryDtoType[];
  isGraphic: boolean;
  onIsGraphicChange: (isGraphic: boolean) => void;
}

export function AlgorithmTargetMapTypeFields({
  algorithmTypeCode,
  maxRuleCode,
  sources,
  mappings,
  isGraphic,
  onIsGraphicChange,
}: AlgorithmTargetMapTypeFieldsProps) {
  const { t } = useTranslation();
  const { setValue } = useFormContext();

  const rules: AlgorithmTargetRuleFormDataType[] = useWatch({ name: 'rules' });

  const onAdd = useCallback(() => {
    const maxCode = getMaxRuleCode(rules);
    const rule = createDefaultTargetRuleFormData(
      AlgorithmTargetRuleType.MAP,
      String(Math.max(maxCode, maxRuleCode) + 1),
    );
    setValue('rules', rules.concat(rule), { shouldDirty: true });
  }, [rules, setValue, maxRuleCode]);

  const onRemove = useCallback(
    (index: number) => {
      setValue('rules', rules.toSpliced(index, 1), { shouldDirty: true });
    },
    [rules, setValue],
  );

  const mapIndexes = useMemo(() => {
    const indexes: number[] = [];
    (rules || []).forEach((rule, index) => {
      if (rule.ruleType === AlgorithmTargetRuleType.MAP) {
        indexes.push(index);
      }
    });
    return indexes;
  }, [rules]);

  const areDbSources = useMemo(
    () =>
      sources.some(
        (item: AlgorithmSourceFormDataType) => item.sourceType === 'DB',
      ),
    [sources],
  );

  const commonProps = {
    algorithmTypeCode,
    onRemove,
    onAdd,
    sources,
    mappings,
    areDbSources,
  };

  const handleModalTypeChange = useCallback(
    (event: RadioChangeEvent) => {
      onIsGraphicChange(event.target.value === 'graph');
    },
    [onIsGraphicChange],
  );

  const sectionBottomRef = useRef<HTMLDivElement | null>(null);
  const prevCountRef = useRef(mapIndexes.length);

  useEffect(() => {
    const prev = prevCountRef.current;

    if (!isGraphic && mapIndexes.length > prev && sectionBottomRef.current) {
      sectionBottomRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }

    prevCountRef.current = mapIndexes.length;
  }, [mapIndexes.length, isGraphic]);

  return (
    <DividerHeader
      id="algorithm-target-map"
      title={t('mapTypeRules.label')}
      count={mapIndexes.length}
      onAddButtonClick={onAdd}
    >
      <Radio.Group
        value={isGraphic ? 'graph' : 'form'}
        onChange={handleModalTypeChange}
        buttonStyle="solid"
        style={{ userSelect: 'none' }}
      >
        <Radio.Button value="form">{t('button.form')}</Radio.Button>
        <Radio.Button value="graph">{t('button.graph')}</Radio.Button>
      </Radio.Group>
      <Flex vertical gap="middle">
        {isGraphic ? (
          <AlgorithmTargetMappingsGraph {...commonProps} rules={rules} />
        ) : (
          <>
            {mapIndexes.map((mapIndex, index) => (
              <AlgorithmTargetMapTypeField
                key={mapIndex}
                index={mapIndex}
                isLast={index >= mapIndexes.length - 1}
                {...commonProps}
              />
            ))}
            <div ref={sectionBottomRef} />
          </>
        )}
      </Flex>
    </DividerHeader>
  );
}
